import { getQuestionTypeWithoutVersion, QuestionType } from './QuestionType';

// BACKEND

export type BackendSolution = {
  question: string;
  type: string;
  url: string;
  is_unanswerable: boolean;
  answers: SolutionAnswer[] | BackendSolutionSubContent[];
};

export type BackendSolutionSubContent = {
  type: string;
  subContent: SolutionAnswer[] | BackendSolutionSubContent[];
  question?: string;
};

// FRONTEND

export type SolutionSubContent = {
  type?: QuestionType;
  subContent: SolutionAnswer[] | SolutionSubContent[];
  question?: string;
};

export type SolutionAnswer = {
  question: string;
  text: string;
  correct: boolean;
};

export type Solution = Omit<BackendSolution, 'type' | 'is_unanswerable' | 'answers'> & {
  type?: QuestionType;
  isUnanswerable: boolean;
  answers: SolutionAnswer[] | SolutionSubContent[];
};

// TYPE GUARDS

export const isSolutionWithSubContent = (
  test: unknown
): test is SolutionSubContent | BackendSolutionSubContent => {
  // Check on type in stead of subContent, because solutions that can't be parsed have subContent: undefined
  const type = (test as SolutionSubContent)?.type;

  return Boolean(
    type != null &&
      Object.values(QuestionType).includes(getQuestionTypeWithoutVersion(type) as QuestionType)
  );
};

// MAPPERS

const mapBackendSolutionSubContentToSolutionSubContent = (
  data: BackendSolutionSubContent
): SolutionSubContent => ({
  subContent:
    data.subContent?.length > 0
      ? isSolutionWithSubContent(data.subContent[0])
        ? (data.subContent as BackendSolutionSubContent[]).map(
            mapBackendSolutionSubContentToSolutionSubContent
          )
        : (data.subContent as SolutionAnswer[])
      : [],
  type: data.type == null ? undefined : getQuestionTypeWithoutVersion(data.type),
  question: data.question,
});

const mapBackendSolutionToSolution = (data: BackendSolution): Solution => ({
  question: data.question,
  type: getQuestionTypeWithoutVersion(data.type),
  url: data.url,
  isUnanswerable: data.is_unanswerable,
  answers:
    data.answers.length === 0
      ? []
      : isSolutionWithSubContent(data.answers[0])
      ? (data.answers as BackendSolutionSubContent[]).map(
          mapBackendSolutionSubContentToSolutionSubContent
        )
      : (data.answers as SolutionAnswer[]),
});

export const solutionsMapper = (response: {
  data: BackendSolution[];
}): Record<string, Solution> => {
  const result: Record<string, Solution> = {};

  response.data.forEach(backendSolution => {
    result[backendSolution.url] = mapBackendSolutionToSolution(backendSolution);
  });

  return result;
};
